import {
  ButtonRecipeVariantProps,
  modal,
  ModalVariant,
} from '@repo/themes/styled-system/recipes';
import { ComponentProps, Fragment, useEffect } from 'react';
import { OverlayLayer } from './OverlayLayer';
import { Slot } from '@radix-ui/react-slot';
import {
  ModalProvider,
  ModalProviderProps,
  useModal,
} from '../contexts/ModalContext';
import { Button } from './Button';
import Flex from './Flex';
import { FlexProps, Stack } from '@repo/themes/styled-system/jsx';
import { css } from '@repo/themes/styled-system/css';
import { SystemProperties } from '@repo/themes/styled-system/types';

const classes = modal();

const ModalRoot = ({
  children,
  open,
  onOpenChange,
  disableOverlay,
  disableScroll,
}: ModalProviderProps) => {
  return (
    <ModalProvider
      open={open}
      onOpenChange={onOpenChange}
      disableOverlay={disableOverlay}
      disableScroll={disableScroll}
    >
      {children}
    </ModalProvider>
  );
};

export interface ModalTriggerPropsType
  extends ComponentProps<'button'>,
    Partial<ButtonRecipeVariantProps> {
  asChild?: boolean;
}

const ModalTrigger = ({
  children,
  asChild,
  ...allProps
}: ModalTriggerPropsType) => {
  const { setOpen } = useModal();
  const Comp = asChild ? Slot : Button;

  return (
    <Comp
      {...allProps}
      onClick={() => {
        setOpen(true);
      }}
    >
      {children}
    </Comp>
  );
};

export interface ModalContentProps extends Partial<ModalVariant>, FlexProps {
  centered?: boolean;
}

const ModalContent = ({
  children,
  centered = true,
  ...allProps
}: ModalContentProps) => {
  const [modalProps, props] = modal.splitVariantProps(allProps);
  const classes = modal(modalProps);
  const { isOpen, disableOverlay, disableScroll } = useModal();

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    if (isOpen && disableScroll) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = previousOverflow;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, disableScroll]);

  const Comp = disableOverlay ? Fragment : OverlayLayer;
  const contentProps = {
    ...props,
    className: classes.content,
    hidden: !isOpen,
  };

  const modalCentering = centered && {
    justify: 'center',
    align: 'center',
  };

  return (
    <Comp hidden={!isOpen}>
      <Stack {...modalCentering} className={css({ height: 'full' })}>
        <Flex {...contentProps}>{children}</Flex>
      </Stack>
    </Comp>
  );
};

export interface ModalHeaderProps extends Partial<ModalVariant>, FlexProps {}

const ModalHeader = ({ children, ...allProps }: ModalHeaderProps) => {
  const [modalProps, props] = modal.splitVariantProps(allProps);
  const classes = modal(modalProps);

  return (
    <Flex className={classes.header} {...props}>
      {children}
    </Flex>
  );
};

export interface ModalDescriptionProps
  extends Partial<ModalVariant>,
    FlexProps {}

const ModalDescription = ({ children, ...allProps }: ModalHeaderProps) => {
  const [modalProps, props] = modal.splitVariantProps(allProps);
  const classes = modal(modalProps);

  const descriptionProps = {
    className: classes.description,
    ...props,
  };

  return <Flex {...descriptionProps}>{children}</Flex>;
};

export interface ModalActionFooterProps
  extends Partial<ModalVariant>,
    FlexProps {
  justify?: SystemProperties['justifyContent'];
  align?: SystemProperties['alignItems'];
}

const ModalActionFooter = ({
  children,
  justify = 'end',
  align = 'end',
  ...allProps
}: ModalActionFooterProps) => {
  return (
    <Flex
      direction={{ base: 'column', sm: 'row' }}
      gap={{ base: '8', sm: '16' }}
      justify={justify}
      align={align}
      {...allProps}
    >
      {children}
    </Flex>
  );
};

export interface ModalActionPropsType
  extends ComponentProps<'button'>,
    Partial<ButtonRecipeVariantProps> {
  isCloseAction?: boolean;
  onAction?: VoidFunction;
  asChild?: boolean;
}

const ModalAction = ({
  children,
  asChild,
  isCloseAction,
  onClick,
  ...allProps
}: ModalActionPropsType) => {
  const Comp = asChild ? Slot : Button;
  const { setOpen } = useModal();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onClick?.(e);
    if (isCloseAction) setOpen(false);
  };

  return (
    <Comp {...allProps} onClick={handleClick}>
      {children}
    </Comp>
  );
};

const ModalClose = ({ children }: { children: React.ReactNode }) => {
  const { setOpen } = useModal();

  return (
    <Slot
      className={classes.close}
      onClick={() => {
        setOpen(false);
      }}
    >
      {children}
    </Slot>
  );
};

ModalTrigger.displayName = 'Modal.Trigger';
ModalContent.displayName = 'Modal.Content';
ModalHeader.displayName = 'Modal.Header';
ModalDescription.displayName = 'Modal.Description';
ModalActionFooter.displayName = 'Modal.ActionFooter';
ModalAction.displayName = 'Modal.Action';
ModalClose.displayName = 'Modal.Close';

export default {
  Root: ModalRoot,
  Trigger: ModalTrigger,
  Content: ModalContent,
  Header: ModalHeader,
  Description: ModalDescription,
  ActionFooter: ModalActionFooter,
  Action: ModalAction,
  Close: ModalClose,
};
